/**
 * TokenCountMeter — thin row showing estimated token usage for the composer.
 *
 * Wave 2C (chat-ui-improvements).
 *
 * Renders a Progress bar + Badge label: "1,234 / 8,000 tokens"
 *
 * Color tiers (per spec §3.4):
 *   - Normal  (< 75 %): muted / default styling
 *   - Amber   (≥ 75 %): amber / warning colors
 *   - Red     (≥ 90 %): red / destructive colors
 *
 * ## Design token note
 * Wave 2A may ship `lib/design-tokens.ts`. If that file doesn't exist when
 * this component is evaluated the raw Tailwind class strings below are used.
 * TODO: migrate to design-tokens.ts when Wave 2A lands.
 *
 * ## Spec
 * CHAT-UI-IMPROVEMENT-DESIGN.md §3.4 — Token meter.
 */

import { useMemo } from 'react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  defaultTokenEstimator,
  DEFAULT_TOKEN_LIMIT,
  tokenMeterColorTier,
  type TokenEstimator,
} from '@/lib/token-estimator';

// ── Props ────────────────────────────────────────────────────────────────────

export interface TokenCountMeterProps {
  /** The current composer text. Updated on every keystroke. */
  text: string;
  /**
   * Maximum token budget to display. Defaults to `DEFAULT_TOKEN_LIMIT` (8 000).
   * Pass the selected model's context window here once that data is available.
   */
  tokenLimit?: number;
  /**
   * Token estimator implementation. Defaults to `defaultTokenEstimator`
   * (Q4 = `Math.ceil(text.length / 4)`). Inject a tiktoken estimator here
   * when the WASM bundle ships.
   */
  estimator?: TokenEstimator;
  /** Extra class names applied to the outer wrapper div. */
  className?: string;
  /** Model id forwarded to the estimator (no-op for Q4 v1). */
  modelId?: string;
}

// ── Color maps ───────────────────────────────────────────────────────────────
// Raw Tailwind classes — no design-token dependency in Wave 2C.
// TODO: migrate to design-tokens.ts when Wave 2A lands.

const PROGRESS_INDICATOR_CLASS: Record<'normal' | 'amber' | 'red', string> = {
  // The Progress component renders an inner div for the bar. We override
  // it via a data-attribute selector trick combined with className on the
  // wrapper + a CSS variable approach, or simply accept the default primary
  // for normal and re-skin the bar via cn overrides.
  normal: '',
  amber: 'text-amber-600',
  red: 'text-red-600',
};

const BADGE_VARIANT: Record<
  'normal' | 'amber' | 'red',
  React.ComponentProps<typeof Badge>['variant']
> = {
  normal: 'secondary',
  amber: 'warning',
  red: 'destructive',
};

const PROGRESS_TRACK_CLASS: Record<'normal' | 'amber' | 'red', string> = {
  normal: '',
  // The Progress track bg is set to `bg-primary/20` inside the component;
  // we tint the bar fill by wrapping in a container with a custom CSS class.
  amber: '[&>div]:bg-amber-500',
  red: '[&>div]:bg-red-500',
};

// ── Component ────────────────────────────────────────────────────────────────

import type React from 'react';

/**
 * Token count meter for the Ask Argus composer.
 *
 * Only renders when `text` is non-empty to keep the composer clean when idle.
 * When the text is empty the component returns `null`.
 */
export function TokenCountMeter({
  text,
  tokenLimit = DEFAULT_TOKEN_LIMIT,
  estimator = defaultTokenEstimator,
  className,
  modelId,
}: TokenCountMeterProps) {
  const tokenCount = useMemo(
    () => estimator.estimate(text, modelId),
    [text, estimator, modelId],
  );

  // Don't render when empty — keeps the composer visually clean on load.
  if (!text) return null;

  const ratio = Math.min(tokenCount / tokenLimit, 1);
  const tier = tokenMeterColorTier(ratio);
  const percentage = Math.round(ratio * 100);

  const label = `${tokenCount.toLocaleString()} / ${tokenLimit.toLocaleString()} tokens`;
  const ariaLabel = `${percentage}% of token budget used — ${label}`;

  return (
    <div
      className={cn('flex items-center gap-2 px-1', className)}
      role="status"
      aria-label={ariaLabel}
      data-testid="token-count-meter"
    >
      {/* Progress bar — spans most of the row */}
      <Progress
        value={tokenCount}
        max={tokenLimit}
        className={cn(
          'h-1 flex-1 min-w-0',
          // Tint the bar fill based on utilization tier.
          PROGRESS_TRACK_CLASS[tier],
        )}
        aria-hidden="true"
      />

      {/* Badge label */}
      <Badge
        variant={BADGE_VARIANT[tier]}
        className={cn(
          'text-[10px] h-4 px-1.5 font-mono whitespace-nowrap shrink-0',
          PROGRESS_INDICATOR_CLASS[tier],
        )}
        data-testid="token-count-badge"
        title={ariaLabel}
      >
        {label}
      </Badge>
    </div>
  );
}
